<script>
import { defineAsyncComponent, useTemplateRef } from 'vue';
import { useMediaQuery } from '@vueuse/core';
import { Dropdown, recomputeAllPoppers } from 'floating-vue';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useTrack } from 'dashboard/composables';
import keyboardEventListenerMixins from 'shared/mixins/keyboardEventListenerMixins';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import ReplyToMessage from './ReplyToMessage.vue';
import AttachmentPreview from 'dashboard/components/widgets/AttachmentsPreview.vue';
import ReplyTopPanel from 'dashboard/components/widgets/WootWriter/ReplyTopPanel.vue';
import ReplyEmailHead from './ReplyEmailHead.vue';
import ReplyBottomPanel from 'dashboard/components/widgets/WootWriter/ReplyBottomPanel.vue';
import CopilotReplyBottomPanel from 'dashboard/components/widgets/WootWriter/CopilotReplyBottomPanel.vue';
import AceleresTeamPresenceAPI from 'dashboard/api/aceleresTeamPresence';
import AceleresCannedPopover from 'dashboard/components/widgets/WootWriter/AceleresCannedPopover.vue';
import ArticleSearchPopover from 'dashboard/routes/dashboard/helpcenter/components/ArticleSearch/SearchPopover.vue';
import CopilotEditorSection from './CopilotEditorSection.vue';
import ReplyBoxBanner from './ReplyBoxBanner.vue';
import SessionExpiredPanel from './SessionExpiredPanel.vue';
import AceleresWebhookSessionExpiredPanel from './AceleresWebhookSessionExpiredPanel.vue';
import AceleresWhatsappDisconnectedPanel from './AceleresWhatsappDisconnectedPanel.vue';
import QuotedEmailPreview from './QuotedEmailPreview.vue';
import { REPLY_EDITOR_MODES } from 'dashboard/components/widgets/WootWriter/constants';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import AudioRecorder from 'dashboard/components/widgets/WootWriter/AudioRecorder.vue';
import { AUDIO_FORMATS } from 'shared/constants/messages';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { CMD_AI_ASSIST } from 'dashboard/helper/commandbar/events';
import {
  getMessageVariables,
  getUndefinedVariablesInMessage,
} from '@chatwoot/utils';
import WhatsappTemplates from './WhatsappTemplates/Modal.vue';
import ContentTemplates from './ContentTemplates/ContentTemplatesModal.vue';
import { MESSAGE_MAX_LENGTH } from 'shared/helpers/MessageTypeHelper';
import inboxMixin, { INBOX_FEATURES } from 'shared/mixins/inboxMixin';
import { trimContent, debounce, getRecipients } from '@chatwoot/utils';
import wootConstants from 'dashboard/constants/globals';
import {
  extractQuotedEmailText,
  buildQuotedEmailHeader,
  truncatePreviewText,
  appendQuotedTextToMessage,
} from 'dashboard/helper/quotedEmailHelper';
import {
  CONVERSATION_EVENTS,
  CAPTAIN_EVENTS,
} from '../../../helper/AnalyticsHelper/events';
import fileUploadMixin from 'dashboard/mixins/fileUploadMixin';
import {
  appendSignature,
  removeSignature,
  getEffectiveChannelType,
} from 'dashboard/helper/editorHelper';
import { useCopilotReply } from 'dashboard/composables/useCopilotReply';
import { useKbd } from 'dashboard/composables/utils/useKbd';
import { isFileTypeAllowedForChannel } from 'shared/helpers/FileHelper';
import {
  createPdfFirstPagePreview,
  isPdfFile,
} from 'dashboard/helper/pdfPreviewHelper';
import {
  isAceleresConversationStarted,
  isAceleresPersonalConversation,
} from 'dashboard/helper/aceleresConversationState';
import { isTrainingConversation } from 'dashboard/helper/trainingConversation';

import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { LocalStorage } from 'shared/helpers/localStorage';
import { emitter } from 'shared/helpers/mitt';

const EmojiInput = defineAsyncComponent(
  () => import('shared/components/emoji/EmojiInput.vue')
);

export default {
  components: {
    ArticleSearchPopover,
    AttachmentPreview,
    AudioRecorder,
    ReplyBoxBanner,
    AceleresWebhookSessionExpiredPanel,
    AceleresWhatsappDisconnectedPanel,
    SessionExpiredPanel,
    EmojiInput,
    ReplyBottomPanel,
    ReplyEmailHead,
    ReplyToMessage,
    ReplyTopPanel,
    ContentTemplates,
    WhatsappTemplates,
    WootMessageEditor,
    QuotedEmailPreview,
    CopilotEditorSection,
    CopilotReplyBottomPanel,
    AceleresCannedPopover,
    Dropdown,
  },
  mixins: [inboxMixin, fileUploadMixin, keyboardEventListenerMixins],
  emits: ['toggleEditorSize'],
  setup() {
    const {
      uiSettings,
      isEditorHotKeyEnabled,
      fetchSignatureFlagFromUISettings,
      setQuotedReplyFlagForInbox,
      fetchQuotedReplyFlagFromUISettings,
    } = useUISettings();

    const replyEditor = useTemplateRef('replyEditor');
    const messageEditor = useTemplateRef('messageEditor');
    const isMobileViewport = useMediaQuery('(max-width: 767px)');
    const copilot = useCopilotReply();
    const shortcutKey = useKbd(['$mod', '+', 'enter']);

    return {
      uiSettings,
      isEditorHotKeyEnabled,
      fetchSignatureFlagFromUISettings,
      setQuotedReplyFlagForInbox,
      fetchQuotedReplyFlagFromUISettings,
      replyEditor,
      messageEditor,
      isMobileViewport,
      copilot,
      shortcutKey,
    };
  },
  data() {
    return {
      aceleresStarting: false,
      message: '',
      inReplyTo: {},
      isFocused: false,
      showEmojiPicker: false,
      // A galeria de mídia é derivada dos anexos. Enquanto o picker de
      // figurinhas estiver aberto, ela fica temporariamente suprimida para
      // que os dois painéis nunca ocupem o composer ao mesmo tempo.
      suppressWhatsAppGallery: false,
      attachedFiles: [],
      pendingAttachmentUploads: 0,
      isRecordingAudio: false,
      recordingAudioState: '',
      isRecordingPaused: false,
      recordingAudioDurationText: '',
      // Aceleres: true quando o usuário clicou em Enviar durante a gravação
      // (WhatsApp). O gatilho do envio vive no attachFile (não num watcher —
      // em Vue 3 watcher de array reativo não dispara no push). Ver
      // sendAudioRecording + attachFile.
      sendOnFinish: false,
      replyType: REPLY_EDITOR_MODES.REPLY,
      bccEmails: '',
      ccEmails: '',
      toEmails: '',
      doAutoSaveDraft: () => {},
      showWhatsAppTemplatesModal: false,
      showContentTemplatesModal: false,
      updateEditorSelectionWith: '',
      undefinedVariableMessage: '',
      showMentions: false,
      showUserMentions: false,
      showCannedMenu: false,
      showAceleresCannedPopover: false,
      showVariablesMenu: false,
      newConversationModalActive: false,
      showArticleSearchPopover: false,
      hasRecordedAudio: false,
      copilotAcceptedMessages: {},
      isReplyBoxMultiline: false,
      replyBoxResizeObserver: null,
      replyBoxResizeFrame: null,
      replyBoxKeydownCapture: null,
      isSendingAttachments: false,
      isSendingGif: false,
      attachmentSendProgress: { current: 0, total: 0, active: 0 },
      inboxConnectionVerifiedId: null,
      isConversationFileDragActive: false,
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      messageSignature: 'getMessageSignature',
      currentUser: 'getCurrentUser',
      lastEmail: 'getLastEmailInSelectedChat',
      globalConfig: 'globalConfig/get',
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
    }),
    isCurrentAccountSeller() {
      const currentAccount = this.currentUser?.accounts?.find(
        account => Number(account.id) === Number(this.accountId)
      );

      return currentAccount?.role === 'agent';
    },
    currentContact() {
      const senderId = this.currentChat?.meta?.sender?.id;
      if (!senderId) return {};
      return this.$store.getters['contacts/getContact'](senderId);
    },
    shouldShowReplyToMessage() {
      return (
        this.inReplyTo?.id &&
        !this.isPrivate &&
        this.inboxHasFeature(INBOX_FEATURES.REPLY_TO) &&
        !this.is360DialogWhatsAppChannel &&
        !this.copilot.isActive.value
      );
    },
    showWhatsappTemplates() {
      // We support templates for API channels if someone updates templates manually via API
      // That's why we don't explicitly check for channel type here
      const templates = this.$store.getters['inboxes/getWhatsAppTemplates'](
        this.inboxId
      );
      return !!(templates && templates.length) && !this.isPrivate;
    },
    showContentTemplates() {
      return this.isATwilioWhatsAppChannel && !this.isPrivate;
    },
    isPrivate() {
      if (
        this.currentChat.can_reply ||
        this.isAWhatsAppChannel ||
        this.isAPIInbox
      ) {
        return this.isOnPrivateNote;
      }
      return true;
    },
    isReplyRestricted() {
      return (
        !this.currentChat?.can_reply &&
        !(this.isAWhatsAppChannel || this.isAPIInbox)
      );
    },
    isTrainingConversationContext() {
      return isTrainingConversation(this.currentChat, this.currentUser);
    },
    inboxId() {
      return this.currentChat.inbox_id;
    },
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.inboxId);
    },
    isWameAPIInbox() {
      return (
        this.isAPIInbox &&
        this.inbox?.additional_attributes?.aceleres_outbound_mode === 'webhook'
      );
    },
    messagePlaceHolder() {
      if (this.isEditorDisabled) {
        if (this.isAWhatsAppChannel) {
          return this.$t('CONVERSATION.FOOTER.MESSAGING_RESTRICTED_WHATSAPP');
        }
        if (this.isAPIInbox) {
          return this.$t('CONVERSATION.FOOTER.MESSAGING_RESTRICTED_API');
        }
        return this.$t('CONVERSATION.FOOTER.MESSAGING_RESTRICTED');
      }
      if (this.isMobileViewport && !this.isPrivate) {
        return 'Digite uma mensagem';
      }
      return this.isPrivate
        ? this.$t('CONVERSATION.FOOTER.PRIVATE_MSG_INPUT')
        : this.$t('CONVERSATION.FOOTER.MSG_INPUT');
    },
    isMessageLengthReachingThreshold() {
      return this.message.length > this.maxLength - 50;
    },
    charactersRemaining() {
      return this.maxLength - this.message.length;
    },
    isReplyButtonDisabled() {
      if (this.isSendingAttachments) return true;
      if (this.isEditorDisabled) return true;
      if (this.isATwitterInbox) return true;
      if (this.hasAttachments || this.hasRecordedAudio) return false;

      return this.isMessageEmpty || this.message.length > this.maxLength;
    },
    sender() {
      return {
        name: this.currentUser.name,
        thumbnail: this.currentUser.avatar_url,
      };
    },
    conversationType() {
      const { additional_attributes: additionalAttributes } = this.currentChat;
      const type = additionalAttributes ? additionalAttributes.type : '';
      return type || '';
    },
    maxLength() {
      if (this.isPrivate) {
        return MESSAGE_MAX_LENGTH.GENERAL;
      }
      if (this.isAFacebookInbox) {
        return MESSAGE_MAX_LENGTH.FACEBOOK;
      }
      if (this.isAnInstagramChannel) {
        return MESSAGE_MAX_LENGTH.INSTAGRAM;
      }
      if (this.isATelegramChannel) {
        return MESSAGE_MAX_LENGTH.TELEGRAM;
      }
      if (this.isATiktokChannel) {
        return MESSAGE_MAX_LENGTH.TIKTOK;
      }
      if (this.isATwilioWhatsAppChannel) {
        return MESSAGE_MAX_LENGTH.TWILIO_WHATSAPP;
      }
      if (this.isAWhatsAppCloudChannel) {
        return MESSAGE_MAX_LENGTH.WHATSAPP_CLOUD;
      }
      if (this.isASmsInbox) {
        return MESSAGE_MAX_LENGTH.TWILIO_SMS;
      }
      if (this.isAnEmailChannel) {
        return MESSAGE_MAX_LENGTH.EMAIL;
      }
      if (this.isATwilioSMSChannel) {
        return MESSAGE_MAX_LENGTH.TWILIO_SMS;
      }
      if (this.isAWhatsAppChannel) {
        return MESSAGE_MAX_LENGTH.WHATSAPP_CLOUD;
      }
      return MESSAGE_MAX_LENGTH.GENERAL;
    },
    showFileUpload() {
      return (
        this.isAWebWidgetInbox ||
        this.isAFacebookInbox ||
        this.isAWhatsAppChannel ||
        this.isAPIInbox ||
        this.isAnEmailChannel ||
        this.isASmsInbox ||
        this.isATelegramChannel ||
        this.isALineChannel ||
        this.isAnInstagramChannel ||
        this.isATiktokChannel
      );
    },
    replyButtonLabel() {
      let sendMessageText = this.$t('CONVERSATION.REPLYBOX.SEND');
      if (this.isPrivate) {
        sendMessageText = this.$t('CONVERSATION.REPLYBOX.CREATE');
      }
      const keyLabel = this.isEditorHotKeyEnabled('cmd_enter')
        ? `(${this.shortcutKey})`
        : '(↵)';
      return `${sendMessageText} ${keyLabel}`;
    },
    replyBoxClass() {
      return {
        'is-private': this.isPrivate,
        'is-focused': this.isFocused || this.hasAttachments,
        'reply-box--multi-line': this.isReplyBoxMultiline,
      };
    },
    hasAttachments() {
      return this.attachedFiles.length;
    },
    hasCannedResponseAttachments() {
      return this.attachedFiles.some(
        attachment => attachment.isCannedResponseAttachment
      );
    },
    hasPreviewableAttachments() {
      return this.attachedFiles.some(file => !file?.isRecordedAudio);
    },
    usesMediaGallery() {
      return (
        this.hasAttachments &&
        this.hasPreviewableAttachments &&
        !this.isOnPrivateNote
      );
    },
    isOnWhatsApp() {
      // Channel::Api is included because in this install Api inboxes route
      // to WhatsApp via Evolution. The send/preview flow matches WhatsApp:
      // each attachment is sent as its own message with its own caption.
      return (
        this.isATwilioWhatsAppChannel ||
        this.isAWhatsAppCloudChannel ||
        this.is360DialogWhatsAppChannel ||
        this.isAPIInbox
      );
    },
    showWhatsAppGallery() {
      // Todo anexo público usa a mesma prévia contextual. Áudios gravados
      // continuam no gravador, pois o fluxo deles envia assim que termina.
      return this.usesMediaGallery && !this.suppressWhatsAppGallery;
    },
    showAudioRecorder() {
      return !this.isOnPrivateNote && this.showFileUpload;
    },
    showAudioRecorderEditor() {
      return this.showAudioRecorder && this.isRecordingAudio;
    },
    isOnPrivateNote() {
      return this.replyType === REPLY_EDITOR_MODES.NOTE;
    },
    isOnExpandedLayout() {
      const {
        LAYOUT_TYPES: { CONDENSED },
      } = wootConstants;
      const { conversation_display_type: conversationDisplayType = CONDENSED } =
        this.uiSettings;
      return conversationDisplayType !== CONDENSED;
    },
    isMessageEmpty() {
      if (!this.message) {
        return true;
      }
      // O ProseMirror pode manter caracteres invisíveis após apagar o texto.
      // Eles não devem trocar o microfone por um botão de enviar vazio.
      return !this.message.replace(/[\s\u200B-\u200D\uFEFF]/g, '').length;
    },
    showReplyHead() {
      return !this.isOnPrivateNote && this.isAnEmailChannel;
    },
    enableMultipleFileUpload() {
      return (
        this.isAnEmailChannel ||
        this.isAWebWidgetInbox ||
        this.isAPIInbox ||
        this.isAWhatsAppChannel ||
        this.isATelegramChannel
      );
    },
    isSignatureEnabledForInbox() {
      return !this.isPrivate && this.sendWithSignature;
    },
    isSignatureAvailable() {
      return !!this.messageSignature;
    },
    sendWithSignature() {
      return this.fetchSignatureFlagFromUISettings(this.channelType);
    },
    // Aceleres: uma conexão individual própria é pessoal mesmo quando a
    // mensagem ainda não recebeu a etiqueta/atributo de pessoal.
    aceleresIsPersonalConversation() {
      const chat = this.currentChat;
      return isAceleresPersonalConversation({
        conversation: chat,
        inbox: this.inbox,
      });
    },
    // Aceleres 4 caixas: gate do "Iniciar atendimento" — conversa atribuída a
    // mim ou sem atribuição, ainda NÃO iniciada → bloqueia
    // o composer e mostra o botão Iniciar. Conversa pessoal sem atribuição não
    // entra nesse gate, porque continua sendo pessoal do vendedor.
    aceleresNeedsStartGate() {
      const chat = this.currentChat;
      if (!chat || !chat.id) return false;
      const me = this.currentUser && this.currentUser.id;
      const assigneeId = chat.meta?.assignee?.id ?? chat.assignee_id;
      const isMine = Boolean(me) && Number(assigneeId) === Number(me);
      const isUnassigned = !assigneeId;
      const isPersonal = this.aceleresIsPersonalConversation;
      const started = isAceleresConversationStarted(chat);
      // Conversa pessoal nunca passa pelo gate: ela mora na caixa Pessoais,
      // que por regra nao tem assignee nem fluxo de inicio de atendimento.
      return !isPersonal && (isMine || isUnassigned) && !started;
    },
    // A primeira resposta pública também pode assumir uma conversa ainda não
    // iniciada que esteja em Outros. O backend atribui a conversa ao remetente
    // depois que a mensagem é criada.
    aceleresCanSendReply() {
      if (
        !this.isAPIInbox ||
        this.isOnPrivateNote ||
        this.whatsappInstanceDisconnected
      ) {
        return false;
      }

      const assigneeId = this.currentChat?.meta?.assignee?.id;
      const currentUserId = this.currentUser?.id;
      const isStarted = isAceleresConversationStarted(this.currentChat);
      const isPersonal = this.aceleresIsPersonalConversation;
      const canClaimFirstReply =
        !isStarted &&
        !isPersonal &&
        Boolean(assigneeId) &&
        Boolean(currentUserId) &&
        Number(assigneeId) !== Number(currentUserId);

      return !assigneeId || assigneeId === currentUserId || canClaimFirstReply;
    },
    conversationId() {
      return this.currentChat.id;
    },
    conversationIdByRoute() {
      return this.conversationId;
    },
    editorStateId() {
      return `draft-${this.conversationIdByRoute}-${this.replyType}`;
    },
    audioRecordFormat() {
      // A WAME recebe a gravação original e converte para OGG/Opus no servidor.
      // Evita a conversão MP3 no navegador, que falha em alguns dispositivos.
      if (this.isWameAPIInbox) {
        return AUDIO_FORMATS.WEBM;
      }
      if (this.isAWhatsAppChannel || this.isATelegramChannel) {
        return AUDIO_FORMATS.MP3;
      }
      if (this.isAPIInbox) {
        return AUDIO_FORMATS.MP3;
      }
      return AUDIO_FORMATS.WAV;
    },
    messageVariables() {
      const variables = getMessageVariables({
        conversation: this.currentChat,
        contact: this.currentContact,
        inbox: this.inbox,
      });
      return variables;
    },
    connectedPortalSlug() {
      const { help_center: portal = {} } = this.inbox;
      const { slug = '' } = portal;
      return slug;
    },
    isQuotedEmailReplyEnabled() {
      return this.isFeatureEnabledonAccount(
        this.accountId,
        FEATURE_FLAGS.QUOTED_EMAIL_REPLY
      );
    },
    quotedReplyPreference() {
      if (!this.isAnEmailChannel || !this.isQuotedEmailReplyEnabled) {
        return false;
      }

      return !!this.fetchQuotedReplyFlagFromUISettings(this.channelType);
    },
    lastEmailWithQuotedContent() {
      if (!this.isAnEmailChannel) {
        return null;
      }

      const lastEmail = this.lastEmail;
      if (!lastEmail || lastEmail.private) {
        return null;
      }

      return lastEmail;
    },
    quotedEmailText() {
      return extractQuotedEmailText(this.lastEmailWithQuotedContent);
    },
    quotedEmailPreviewText() {
      return truncatePreviewText(this.quotedEmailText, 80);
    },
    shouldShowQuotedReplyToggle() {
      return (
        this.isAnEmailChannel &&
        !this.isOnPrivateNote &&
        this.isQuotedEmailReplyEnabled
      );
    },
    shouldShowQuotedPreview() {
      return (
        this.shouldShowQuotedReplyToggle &&
        this.quotedReplyPreference &&
        !!this.quotedEmailText
      );
    },
    isDefaultEditorMode() {
      return !this.showAudioRecorderEditor && !this.copilot.isActive.value;
    },
    isEditorDisabled() {
      // A inbox API do treino pode chegar no primeiro payload com
      // can_reply=false. O backend aceita a resposta normalmente; aguardar um
      // reload aqui impedia justamente a primeira mensagem do vendedor.
      if (this.isTrainingConversationContext) return false;

      // No fluxo Aceleres, a primeira resposta também inicia o atendimento:
      // o callback do Message atribui a conversa ao vendedor e marca
      // attendance_started. O botão continua visível como alternativa, mas
      // não pode bloquear o editor nem o envio por Enter.
      if (this.aceleresCanSendReply) {
        return false;
      }

      return (
        (this.isAWhatsAppChannel || this.isAPIInbox) &&
        !this.isOnPrivateNote &&
        (!this.currentChat.can_reply || this.whatsappInstanceDisconnected)
      );
    },
    showSessionExpiryPanels() {
      return !this.isOnPrivateNote && !this.whatsappInstanceDisconnected;
    },
    whatsappInstanceDisconnected() {
      const status = this.inbox?.additional_attributes?.connection_status;
      const isWameInbox =
        Number(this.inbox?.additional_attributes?.aceleres_instance_id || 0) >
        0;

      // A listagem de inboxes pode sobreviver no IndexedDB após uma troca de
      // dono. Enquanto a leitura direta da inbox não terminar, não bloqueamos
      // o atendimento WAME com uma desconexão possivelmente antiga.
      if (isWameInbox && this.inboxConnectionVerifiedId !== this.inboxId) {
        return false;
      }

      return [
        'disconnected',
        'closed',
        'logout',
        'logged_out',
        'unauthenticated',
      ].includes(String(status || '').toLowerCase());
    },
  },
  watch: {
    inboxId: {
      handler(inboxId) {
        this.ensureInboxLoaded(inboxId);
      },
      immediate: true,
    },
    // NOTA (Aceleres 2026-07-03): o envio pós-gravação (botão Enviar da barra
    // WhatsApp) NÃO usa watcher. Em Vue 3 um watcher sobre um array reativo
    // não dispara no .push() (a ref do proxy não muda); e mesmo com deep,
    // oldValue === newValue — logo `files.length > oldFiles.length` seria
    // sempre false. O gatilho vive no attachFile (ponto único onde o arquivo
    // cai na bandeja), controlado pela flag sendOnFinish. Ver sendAudioRecording.
    currentChat(conversation, oldConversation) {
      const { can_reply: canReply } = conversation;
      if (oldConversation && oldConversation.id !== conversation.id) {
        // Only update email fields when switching to a completely different conversation (by ID)
        // This prevents overwriting user input (e.g., CC/BCC fields) when performing actions
        // like self-assign or other updates that do not actually change the conversation context
        this.setCCAndToEmailsFromLastChat();
        // Reset Copilot editor state (includes cancelling ongoing generation)
        this.copilot.reset();
      }

      if (this.isOnPrivateNote) {
        return;
      }

      if (canReply || this.isAWhatsAppChannel || this.isAPIInbox) {
        this.replyType = REPLY_EDITOR_MODES.REPLY;
      } else {
        this.replyType = REPLY_EDITOR_MODES.NOTE;
      }

      this.fetchAndSetReplyTo();
    },
    // When moving from one conversation to another, the store may not have the
    // list of all the messages. A fetch is subsequently made to get the messages.
    // This watcher handles two main cases:
    // 1. When switching conversations and messages are fetched/updated, ensures CC/BCC fields are set from the latest OUTGOING/INCOMING email (not activity/private messages).
    // 2. Fixes and issue where CC/BCC fields could be reset/lost after assignment/activity actions or message mutations that did not represent a true email context change.
    lastEmail: {
      handler(lastEmail) {
        if (!lastEmail) return;
        this.setCCAndToEmailsFromLastChat();
      },
      deep: true,
    },
    conversationIdByRoute(conversationId, oldConversationId) {
      if (conversationId !== oldConversationId) {
        this.setToDraft(oldConversationId, this.replyType);
        this.getFromDraft();
        this.resetRecorderAndClearAttachments();
      }
    },
    message() {
      // Autosave the current message draft.
      this.doAutoSaveDraft();
      this.$nextTick(() => this.syncReplyBoxMultilineState());
    },
    replyType(updatedReplyType, oldReplyType) {
      this.setToDraft(this.conversationIdByRoute, oldReplyType);
      this.getFromDraft();
    },
  },

  mounted() {
    this.getFromDraft();
    // O WootMessageEditor emite o envio por teclado diretamente para este
    // componente, inclusive quando o editor está focado.
    document.addEventListener('paste', this.onPaste);
    this.replyBoxKeydownCapture = this.onReplyBoxKeydownCapture.bind(this);
    document.addEventListener('keydown', this.replyBoxKeydownCapture, true);
    document.addEventListener('dragenter', this.handleConversationDragEnter);
    document.addEventListener('dragover', this.handleConversationDragOver);
    document.addEventListener('dragleave', this.handleConversationDragLeave);
    document.addEventListener('drop', this.handleConversationDrop);
    this.setCCAndToEmailsFromLastChat();
    this.doAutoSaveDraft = debounce(
      () => {
        this.saveDraft(this.conversationIdByRoute, this.replyType);
      },
      500,
      true
    );

    this.fetchAndSetReplyTo();
    emitter.on(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, this.onReplyToMessage);

    // A hacky fix to solve the drag and drop
    // Is showing on top of new conversation modal drag and drop
    // TODO need to find a better solution
    emitter.on(
      BUS_EVENTS.NEW_CONVERSATION_MODAL,
      this.onNewConversationModalActive
    );
    emitter.on(BUS_EVENTS.INSERT_INTO_NORMAL_EDITOR, this.addIntoEditor);
    emitter.on(CMD_AI_ASSIST, this.executeCopilotAction);
    emitter.on(BUS_EVENTS.ADD_MESSAGE_TO_NOTE, this.onAddMessageToNote);
    emitter.on(
      BUS_EVENTS.EMOJI_PICKER_OPENED,
      this.onEmojiPickerOpened
    );
    this.$nextTick(() => this.setupReplyBoxResizeObserver());
    window.addEventListener('resize', this.refreshEmojiPickerPosition);
    window.addEventListener('scroll', this.refreshEmojiPickerPosition, true);
    window.visualViewport?.addEventListener(
      'resize',
      this.refreshEmojiPickerPosition
    );
    window.visualViewport?.addEventListener(
      'scroll',
      this.refreshEmojiPickerPosition
    );
  },
  unmounted() {
    document.removeEventListener('paste', this.onPaste);
    if (this.replyBoxKeydownCapture) {
      document.removeEventListener(
        'keydown',
        this.replyBoxKeydownCapture,
        true
      );
      this.replyBoxKeydownCapture = null;
    }
    document.removeEventListener('dragenter', this.handleConversationDragEnter);
    document.removeEventListener('dragover', this.handleConversationDragOver);
    document.removeEventListener('dragleave', this.handleConversationDragLeave);
    document.removeEventListener('drop', this.handleConversationDrop);
    emitter.off(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, this.onReplyToMessage);
    emitter.off(BUS_EVENTS.INSERT_INTO_NORMAL_EDITOR, this.addIntoEditor);
    emitter.off(BUS_EVENTS.ADD_MESSAGE_TO_NOTE, this.onAddMessageToNote);
    emitter.off(
      BUS_EVENTS.EMOJI_PICKER_OPENED,
      this.onEmojiPickerOpened
    );
    emitter.off(
      BUS_EVENTS.NEW_CONVERSATION_MODAL,
      this.onNewConversationModalActive
    );
    emitter.off(CMD_AI_ASSIST, this.executeCopilotAction);
    this.disconnectReplyBoxResizeObserver();
    window.removeEventListener('resize', this.refreshEmojiPickerPosition);
    window.removeEventListener('scroll', this.refreshEmojiPickerPosition, true);
    window.visualViewport?.removeEventListener(
      'resize',
      this.refreshEmojiPickerPosition
    );
    window.visualViewport?.removeEventListener(
      'scroll',
      this.refreshEmojiPickerPosition
    );
  },
  methods: {
    isFileDragEvent(event) {
      const dataTransfer = event?.dataTransfer;
      if (!dataTransfer) return false;

      if (dataTransfer.files?.length) return true;

      return Array.from(dataTransfer.types || []).includes('Files');
    },
    isConversationDropTarget(target) {
      const element = target?.nodeType === 1 ? target : target?.parentElement;

      return Boolean(
        element?.closest?.('.conversation-details-wrap.chat-whats')
      );
    },
    canDropConversationFile() {
      return (
        !this.newConversationModalActive &&
        !this.isEditorDisabled &&
        (this.showFileUpload || this.isPrivate)
      );
    },
    handleConversationDragEnter(event) {
      if (
        !this.canDropConversationFile() ||
        !this.isFileDragEvent(event) ||
        !this.isConversationDropTarget(event.target)
      ) {
        return;
      }

      this.isConversationFileDragActive = true;
    },
    handleConversationDragOver(event) {
      if (
        !this.canDropConversationFile() ||
        !this.isFileDragEvent(event) ||
        !this.isConversationDropTarget(event.target)
      ) {
        return;
      }

      event.preventDefault();
      this.isConversationFileDragActive = true;
    },
    handleConversationDragLeave(event) {
      if (!this.isConversationFileDragActive || !this.isFileDragEvent(event)) {
        return;
      }

      if (
        event.relatedTarget &&
        this.isConversationDropTarget(event.relatedTarget)
      ) {
        return;
      }

      this.isConversationFileDragActive = false;
    },
    handleConversationDrop(event) {
      if (this.isFileDragEvent(event)) {
        this.isConversationFileDragActive = false;
      }
    },
    async ensureInboxLoaded(inboxId) {
      const id = Number(inboxId);
      if (!Number.isFinite(id) || id <= 0) return;

      await this.$store.dispatch('inboxes/fetch', id);
      this.inboxConnectionVerifiedId = id;
    },
    syncReplyBoxMultilineState() {
      const editorElement = this.replyEditor;
      if (!editorElement) return;

      const proseMirror = editorElement.querySelector('.ProseMirror');
      if (!proseMirror) {
        this.isReplyBoxMultiline = false;
        return;
      }

      const styles = window.getComputedStyle(proseMirror);
      const lineHeight = Number.parseFloat(styles.lineHeight) || 20;
      const verticalPadding =
        (Number.parseFloat(styles.paddingTop) || 0) +
        (Number.parseFloat(styles.paddingBottom) || 0);
      const contentHeight = Math.max(
        0,
        proseMirror.scrollHeight - verticalPadding
      );

      // Aceleres: evita o "tremor" dos ícones laterais enquanto o ProseMirror
      // recalcula altura. Sem histerese, o scrollHeight oscila no limite entre
      // 1 e 2 linhas e a classe reply-box--multi-line liga/desliga no mesmo
      // trecho de digitação.
      const enterMultilineAt = lineHeight * 1.65;
      const leaveMultilineAt = lineHeight * 1.2;
      const nextIsMultiline = this.isReplyBoxMultiline
        ? contentHeight > leaveMultilineAt
        : contentHeight > enterMultilineAt;

      if (nextIsMultiline !== this.isReplyBoxMultiline) {
        this.isReplyBoxMultiline = nextIsMultiline;
      }
    },
    setupReplyBoxResizeObserver() {
      this.disconnectReplyBoxResizeObserver();
      const editorElement = this.replyEditor;
      if (!editorElement) return;

      if (typeof window === 'undefined' || !window.ResizeObserver) {
        this.syncReplyBoxMultilineState();
        return;
      }

      this.replyBoxResizeObserver = new window.ResizeObserver(() => {
        if (this.replyBoxResizeFrame) {
          window.cancelAnimationFrame(this.replyBoxResizeFrame);
        }
        this.replyBoxResizeFrame = window.requestAnimationFrame(() => {
          this.replyBoxResizeFrame = null;
          this.syncReplyBoxMultilineState();
        });
      });
      this.replyBoxResizeObserver.observe(editorElement);
      this.syncReplyBoxMultilineState();
    },
    disconnectReplyBoxResizeObserver() {
      if (this.replyBoxResizeFrame) {
        window.cancelAnimationFrame(this.replyBoxResizeFrame);
        this.replyBoxResizeFrame = null;
      }
      this.replyBoxResizeObserver?.disconnect();
      this.replyBoxResizeObserver = null;
    },
    // Aceleres: botão "Iniciar atendimento" registra o início, trava o rodízio,
    // abre o composer e move de NOVOS para MEUS.
    async aceleresStartConversation({ silent = false } = {}) {
      if (this.aceleresStarting) return false;
      const chat = this.currentChat;
      const conversationDisplayId =
        chat?.display_id ||
        this.$route?.params?.conversation_id ||
        this.$route?.params?.conversationId ||
        this.$route?.params?.id;
      if (!chat || !conversationDisplayId) return false;
      this.aceleresStarting = true;
      try {
        const response = await AceleresTeamPresenceAPI.lockConversation({
          conversationDisplayId,
        });
        const c = this.currentChat || {};
        const currentUpdatedAt = Number(c.updated_at) || 0;
        const updatedAt = Math.max(
          currentUpdatedAt + 1,
          Math.floor(Date.now() / 1000)
        );
        const currentUser = this.currentUser || {};
        const assignee = response?.data?.assignee || {
          id: currentUser.id,
          name: currentUser.name,
          available_name: currentUser.available_name,
          email: currentUser.email,
          thumbnail: currentUser.thumbnail,
        };
        this.$store.dispatch('updateConversation', {
          ...c,
          updated_at: updatedAt,
          meta: {
            ...(c.meta || {}),
            assignee,
          },
          additional_attributes: {
            ...(c.additional_attributes || {}),
            attendance_started: true,
            rodizio_locked: true,
            rodizio_locked_by: currentUser.id,
            rodizio_locked_reason: 'iniciar_button',
            rodizio_locked_at: new Date().toISOString(),
          },
        });
        await this.$store.dispatch('getConversation', c.id);
        emitter.emit(BUS_EVENTS.ACELERES_CONVERSATION_STARTED, {
          conversationId: c.id,
          displayId: conversationDisplayId,
        });
        if (!silent) useAlert('Atendimento iniciado e movido para Meus.');
        return true;
      } catch (e) {
        if (!silent) {
          useAlert(
            e?.response?.data?.error ||
              'Não foi possível iniciar o atendimento. Tente novamente.'
          );
        }
        return false;
      } finally {
        this.aceleresStarting = false;
      }
    },
    // Aceleres: abre nota privada pre-preenchida com o texto da mensagem
    onAddMessageToNote({ content, conversationId }) {
      if (this.conversationIdByRoute !== conversationId) return;
      this.replyType = REPLY_EDITOR_MODES.NOTE;
      this.$nextTick(() => {
        if (content) this.message = content;
      });
    },
    getDraftKey(
      conversationId = this.conversationIdByRoute,
      replyType = this.replyType
    ) {
      return `draft-${conversationId}-${replyType}`;
    },
    getCopilotAcceptedMessage(replyType = this.replyType) {
      const key = this.getDraftKey(this.conversationIdByRoute, replyType);
      return this.copilotAcceptedMessages[key] || '';
    },
    setCopilotAcceptedMessage(message, replyType = this.replyType) {
      const key = this.getDraftKey(this.conversationIdByRoute, replyType);
      this.copilotAcceptedMessages[key] = trimContent(
        message || '',
        this.maxLength
      );
    },
    clearCopilotAcceptedMessage(replyType = this.replyType) {
      const key = this.getDraftKey(this.conversationIdByRoute, replyType);
      delete this.copilotAcceptedMessages[key];
    },
    handleInsert(article) {
      const { url, title } = article;
      // Removing empty lines from the title
      const lines = title.split('\n');
      const nonEmptyLines = lines.filter(line => line.trim() !== '');
      const filteredMarkdown = nonEmptyLines.join(' ');
      emitter.emit(
        BUS_EVENTS.INSERT_INTO_RICH_EDITOR,
        `[${filteredMarkdown}](${url})`
      );

      useTrack(CONVERSATION_EVENTS.INSERT_ARTICLE_LINK);
    },
    toggleQuotedReply() {
      if (!this.isAnEmailChannel) {
        return;
      }

      const nextValue = !this.quotedReplyPreference;
      this.setQuotedReplyFlagForInbox(this.channelType, nextValue);
    },
    shouldIncludeQuotedEmail() {
      return (
        this.isQuotedEmailReplyEnabled &&
        this.quotedReplyPreference &&
        this.shouldShowQuotedReplyToggle &&
        !!this.quotedEmailText
      );
    },
    getMessageWithQuotedEmailText(message) {
      if (!this.shouldIncludeQuotedEmail()) {
        return message;
      }

      const quotedText = this.quotedEmailText || '';
      const header = buildQuotedEmailHeader(
        this.lastEmailWithQuotedContent,
        this.currentContact,
        this.inbox
      );

      return appendQuotedTextToMessage(message, quotedText, header);
    },
    resetRecorderAndClearAttachments() {
      // Reset audio recorder UI state
      this.resetAudioRecorderInput();
      // Reset attached files
      this.attachedFiles = [];
    },
    saveDraft(conversationId, replyType) {
      if (this.message || this.message === '') {
        const key = this.getDraftKey(conversationId, replyType);
        const draftToSave = trimContent(this.message || '', this.maxLength);

        this.$store.dispatch('draftMessages/set', {
          key,
          message: draftToSave,
        });
      }
    },
    setToDraft(conversationId, replyType) {
      this.saveDraft(conversationId, replyType);
      this.message = '';
    },
    getFromDraft() {
      if (this.conversationIdByRoute) {
        const key = this.getDraftKey();
        const messageFromStore =
          this.$store.getters['draftMessages/get'](key) || '';

        // ensure that the message has signature set based on the ui setting
        this.message = this.toggleSignatureForDraft(messageFromStore);
      }
    },
    toggleSignatureForDraft(message) {
      if (this.isPrivate) {
        return message;
      }

      // Even when editor is disabled (e.g. WhatsApp/API can't reply), we must
      // still normalize stale signatures out of drafts when signature is off.
      if (this.isEditorDisabled && this.sendWithSignature) {
        return message;
      }

      const effectiveChannelType = getEffectiveChannelType(
        this.channelType,
        this.inbox?.medium || ''
      );

      return this.sendWithSignature
        ? appendSignature(message, this.messageSignature, effectiveChannelType)
        : removeSignature(message, this.messageSignature, effectiveChannelType);
    },
    removeFromDraft() {
      if (this.conversationIdByRoute) {
        const key = this.getDraftKey();
        this.$store.dispatch('draftMessages/delete', { key });
      }
    },
    getComposerSnapshot() {
      return {
        message: this.message,
        attachedFiles: [...this.attachedFiles],
        inReplyTo: this.inReplyTo,
        ccEmails: this.ccEmails,
        bccEmails: this.bccEmails,
        toEmails: this.toEmails,
      };
    },
    clearComposerAfterSend() {
      this.clearMessage();
      this.removeFromDraft();
      this.$refs.messageEditor?.forceSetContent?.(this.message || '');
      this.isReplyBoxMultiline = false;
    },
    restoreComposerSnapshot(snapshot, { preserveAttachedFiles = false } = {}) {
      if (!snapshot) return;
      this.message = snapshot.message;
      if (!preserveAttachedFiles) {
        this.attachedFiles = snapshot.attachedFiles;
      }
      this.inReplyTo = snapshot.inReplyTo;
      this.ccEmails = snapshot.ccEmails;
      this.bccEmails = snapshot.bccEmails;
      this.toEmails = snapshot.toEmails;
      this.$nextTick(() => {
        this.$refs.messageEditor?.forceSetContent?.(this.message || '');
        this.syncReplyBoxMultilineState();
        this.saveDraft(this.conversationIdByRoute, this.replyType);
      });
    },
    getElementToBind() {
      return this.replyEditor;
    },
    getKeyboardEvents() {
      return {
        Escape: {
          action: () => {
            this.hideEmojiPicker();
          },
          allowOnFocusedInput: true,
        },
        '$mod+KeyK': {
          action: e => {
            e.preventDefault();
            if (this.isCurrentAccountSeller) return;

            const ninja = document.querySelector('ninja-keys');
            ninja.open();
          },
          allowOnFocusedInput: true,
        },
        Enter: {
          action: e => {
            const isReplyEditorTarget = this.replyEditor?.contains(e.target);
            const canSendFromReplyEditor =
              isReplyEditorTarget &&
              !this.isEditorDisabled &&
              !this.showUserMentions &&
              !this.showMentions &&
              !this.showCannedMenu &&
              !this.showVariablesMenu &&
              this.isEditorHotKeyEnabled('enter');

            if (canSendFromReplyEditor || this.isAValidEvent('enter')) {
              this.onSendReply();
              e.preventDefault();
            }
          },
          allowOnFocusedInput: true,
        },
        '$mod+Enter': {
          action: () => {
            if (this.copilot.isActive.value && this.isFocused) {
              this.onSubmitCopilotReply();
            } else if (this.isAValidEvent('cmd_enter')) {
              this.onSendReply();
            }
          },
          allowOnFocusedInput: true,
        },
      };
    },
    isAValidEvent(selectedKey) {
      return (
        !this.showUserMentions &&
        !this.showMentions &&
        !this.showCannedMenu &&
        !this.showVariablesMenu &&
        this.isFocused &&
        this.isEditorHotKeyEnabled(selectedKey)
      );
    },
    onPaste(e) {
      // Don't handle paste if compose new conversation modal is open
      if (this.newConversationModalActive) return;

      // Don't handle paste if editor is disabled
      if (this.isEditorDisabled) return;

      // Filter valid files (non-zero size)
      Array.from(e.clipboardData.files)
        .filter(file => file.size > 0)
        .filter(file => {
          const isAllowed = isFileTypeAllowedForChannel(file, {
            channelType: this.channelType || this.inbox?.channel_type,
            medium: this.inbox?.medium,
            conversationType: this.conversationType,
            isInstagramChannel: this.isAnInstagramChannel,
            isOnPrivateNote: this.isOnPrivateNote,
          });

          if (!isAllowed) {
            useAlert(
              this.$t('CONVERSATION.FILE_TYPE_NOT_SUPPORTED', {
                fileName: file.name,
              })
            );
          }

          return isAllowed;
        })
        .forEach(file => {
          const { name, type, size } = file;
          this.prepareMediaUpload({ name, type, size, file });
        });
    },
    toggleUserMention(currentMentionState) {
      this.showUserMentions = currentMentionState;
    },
    toggleCannedMenu(value) {
      this.showCannedMenu = value;
    },
    toggleAceleresCannedPopover() {
      // Aceleres: botão "Respostas rápidas" no ReplyBottomPanel abre o
      // popover custom (AceleresCannedPopover) que lista as respostas
      // rápidas e permite criar uma nova caso não existam.
      const shouldOpen = !this.showAceleresCannedPopover;
      if (
        shouldOpen &&
        window.matchMedia('(max-width: 767px), (pointer: coarse)').matches
      ) {
        document.activeElement?.blur?.();
      }
      this.showAceleresCannedPopover = shouldOpen;
    },
    closeAceleresCannedPopover() {
      this.showAceleresCannedPopover = false;
    },
    onAceleresCannedInsert(payload) {
      if (!payload || typeof payload.content !== 'string') return;
      // Insere o conteúdo no editor; o popover já fecha sozinho ao emitir.
      emitter.emit(BUS_EVENTS.INSERT_INTO_RICH_EDITOR, payload.content);
      const attachments = Array.isArray(payload.attachments)
        ? payload.attachments
        : [];
      if (attachments.length) {
        attachments.forEach((attachment, index) => {
          this.handleCannedAttachment({
            attachmentUrl:
              attachment.attachment_url || attachment.attachmentUrl || null,
            attachmentSignedId:
              attachment.attachment_signed_id ||
              attachment.attachmentSignedId ||
              null,
            fileName: attachment.file_name || attachment.fileName || '',
            fileType: attachment.file_type || attachment.fileType || '',
            fileSize: attachment.file_size || attachment.fileSize || 0,
            caption: attachment.caption || (index === 0 ? payload.content : ''),
          });
        });
      } else if (payload.attachmentSignedId) {
        this.handleCannedAttachment({
          attachmentUrl: payload.attachmentUrl,
          attachmentSignedId: payload.attachmentSignedId,
          fileName: payload.fileName || '',
          fileType: payload.fileType || '',
          fileSize: payload.fileSize || 0,
          caption: payload.content,
        });
      }
    },
    toggleVariablesMenu(value) {
      this.showVariablesMenu = value;
    },
    openWhatsappTemplateModal() {
      this.showWhatsAppTemplatesModal = true;
    },
    hideWhatsappTemplatesModal() {
      this.showWhatsAppTemplatesModal = false;
    },
    openContentTemplateModal() {
      this.showContentTemplatesModal = true;
    },
    hideContentTemplatesModal() {
      this.showContentTemplatesModal = false;
    },
    async confirmOnSendReply() {
      // Enter executa o mesmo início/atribuição do botão antes de enviar.
      if (this.aceleresNeedsStartGate) {
        const started = await this.aceleresStartConversation({ silent: true });
        if (!started) return false;
      }

      if (this.isReplyButtonDisabled) {
        return false;
      }
      if (
        !this.isPrivate &&
        this.attachedFiles.length > 0 &&
        this.attachedFiles.every(attachment => this.isGifAttachment(attachment))
      ) {
        const sent = await this.sendAttachedGifsDirectly();
        if (!sent) return false;
        this.clearComposerAfterSend();
        this.hideEmojiPicker();
        return true;
      }
      if (!this.showMentions) {
        const copilotAcceptedMessage = this.getCopilotAcceptedMessage();
        const composerSnapshot = this.getComposerSnapshot();
        const editorMessage = this.message;
        // Local alias of the computed `isOnWhatsApp` so this branch follows
        // the gallery flow (one outgoing message per attachment with its own
        // caption). Includes Channel::Api (WhatsApp via Evolution in this install).
        const isOnWhatsApp = this.isOnWhatsApp;
        // Instagram and TikTok do not support sending text and attachments in the same message.
        // For Instagram, combining them causes duplicate messages due to separate echo events per component.
        // For TikTok, the API rejects messages that mix text and media.
        // To handle both cases, text and attachments are always sent as separate messages.
        const isOnInstagram = this.isAnInstagramChannel;
        const isOnTiktok = this.isATiktokChannel;
        const sendsMultipleMessages =
          (this.usesMediaGallery ||
            isOnWhatsApp ||
            isOnInstagram ||
            isOnTiktok ||
            this.hasCannedResponseAttachments) &&
          !this.isPrivate;
        let sentSuccessfully;
        if (sendsMultipleMessages) {
          const messages = this.getMultipleMessagesPayload(editorMessage);
          if (!messages.length) return false;
          this.clearComposerAfterSend();
          sentSuccessfully = await this.sendMessageAsMultipleMessages(
            editorMessage,
            copilotAcceptedMessage,
            messages,
            composerSnapshot.attachedFiles
          );
        } else {
          const messagePayload = this.getMessagePayload(editorMessage);
          this.clearComposerAfterSend();
          sentSuccessfully = await this.sendMessage(
            messagePayload,
            editorMessage,
            copilotAcceptedMessage
          );
        }

        if (!sentSuccessfully) {
          this.restoreComposerSnapshot(composerSnapshot, {
            preserveAttachedFiles: sendsMultipleMessages,
          });
          return false;
        }

        if (!this.isPrivate) {
          this.clearEmailField();
        }

        this.hideEmojiPicker();
        return true;
      }
      return false;
    },
    onEditorSend(event) {
      // O ProseMirror só emite este evento depois de reconhecer um Enter
      // válido. Não depender de isFocused aqui evita perder a primeira
      // mensagem quando o foco ainda não foi refletido no estado do Vue.
      if (
        this.showUserMentions ||
        this.showMentions ||
        this.showCannedMenu ||
        this.showVariablesMenu
      ) {
        return;
      }

      // O editor já recebeu a tecla; evita que o listener global envie duas vezes.
      event?.stopPropagation();
      this.onSendReply();
    },
    onEditorKeydown(event) {
      if (
        event?.key !== 'Enter' ||
        event.shiftKey ||
        event.metaKey ||
        event.ctrlKey
      ) {
        return;
      }

      // Fallback nativo: cobre navegadores em que o handleDOMEvents do
      // ProseMirror não é chamado, mantendo Shift/Ctrl/Command como atalho
      // de quebra/ação alternativa.
      this.onEditorSend(event);
    },
    onReplyBoxKeydownCapture(event) {
      if (
        event?.key !== 'Enter' ||
        event.shiftKey ||
        event.metaKey ||
        event.ctrlKey
      ) {
        return;
      }

      const editorElement = event.target?.closest?.('.ProseMirror');
      if (!editorElement || !this.replyEditor?.contains(editorElement)) {
        return;
      }

      if (
        this.showUserMentions ||
        this.showMentions ||
        this.showCannedMenu ||
        this.showVariablesMenu
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      this.onSendReply();
    },
    async sendMessageAsMultipleMessages(
      message,
      copilotAcceptedMessage = '',
      preparedMessages = null,
      attachedFilesSnapshot = this.attachedFiles
    ) {
      const messages =
        preparedMessages || this.getMultipleMessagesPayload(message);
      if (!messages.length) return false;

      const isSequentialAttachmentBatch =
        (this.usesMediaGallery ||
          this.isOnWhatsApp ||
          this.hasCannedResponseAttachments) &&
        attachedFilesSnapshot.length > 0;
      this.isSendingAttachments = true;
      this.attachmentSendProgress = {
        current: 0,
        total: messages.length,
        active: messages.length ? 1 : 0,
      };

      try {
        let failedMessageIndex = -1;
        const sentSuccessfully = await messages.reduce(
          (previousSend, messagePayload, index) =>
            previousSend.then(async previousWasSent => {
              if (!previousWasSent) return false;
              this.attachmentSendProgress = {
                current: index,
                total: messages.length,
                active: index + 1,
              };
              const currentWasSent = await this.sendMessage(
                messagePayload,
                messagePayload.message || '',
                copilotAcceptedMessage
              );
              if (currentWasSent) {
                this.attachmentSendProgress.current = index + 1;
              }
              if (!currentWasSent) failedMessageIndex = index;
              return currentWasSent;
            }),
          Promise.resolve(true)
        );

        if (!sentSuccessfully && isSequentialAttachmentBatch) {
          const failedPayload = messages[failedMessageIndex];
          if (failedPayload?.files?.length) {
            const failedAttachmentIndex = messages
              .slice(0, failedMessageIndex)
              .filter(payload => payload.files?.length).length;
            this.attachedFiles = attachedFilesSnapshot.slice(
              failedAttachmentIndex
            );
          }
        }

        return sentSuccessfully;
      } finally {
        this.isSendingAttachments = false;
        this.attachmentSendProgress = { current: 0, total: 0, active: 0 };
      }
    },
    sendMessageAnalyticsData(
      isPrivate,
      { editorMessage = '', copilotAcceptedMessage = '' } = {}
    ) {
      const normalizeForComparison = message => {
        let normalizedMessage = message || '';

        if (this.sendWithSignature && this.messageSignature && !isPrivate) {
          const effectiveChannelType = getEffectiveChannelType(
            this.channelType,
            this.inbox?.medium || ''
          );
          normalizedMessage = removeSignature(
            normalizedMessage,
            this.messageSignature,
            effectiveChannelType
          );
        }

        return trimContent(normalizedMessage);
      };

      const normalizedAcceptedMessage = normalizeForComparison(
        copilotAcceptedMessage
      );
      const normalizedEditorMessage = normalizeForComparison(editorMessage);

      if (normalizedAcceptedMessage && normalizedEditorMessage) {
        useTrack(CAPTAIN_EVENTS.AI_ASSISTED_MESSAGE_SENT, {
          conversationId: this.conversationIdByRoute,
          channelType: this.channelType,
          editedBeforeSend:
            normalizedAcceptedMessage !== normalizedEditorMessage,
          isPrivate,
        });
      }

      // Analytics data for message signature is enabled or not in channels
      return isPrivate
        ? useTrack(CONVERSATION_EVENTS.SENT_PRIVATE_NOTE)
        : useTrack(CONVERSATION_EVENTS.SENT_MESSAGE, {
            channelType: this.channelType,
            signatureEnabled: this.sendWithSignature,
            hasReplyTo: !!this.inReplyTo?.id,
          });
    },
    async onSendReply() {
      const undefinedVariables = getUndefinedVariablesInMessage({
        message: this.message,
        variables: this.messageVariables,
      });
      if (undefinedVariables.length > 0) {
        const undefinedVariablesCount =
          undefinedVariables.length > 1 ? undefinedVariables.length : 1;
        this.undefinedVariableMessage = this.$t(
          'CONVERSATION.REPLYBOX.UNDEFINED_VARIABLES.MESSAGE',
          {
            undefinedVariablesCount,
            undefinedVariables: undefinedVariables.join(', '),
          }
        );

        const ok = await this.$refs.confirmDialog.showConfirmation();
        if (ok) {
          await this.confirmOnSendReply();
        }
      } else {
        await this.confirmOnSendReply();
      }
    },
    async sendMessage(
      messagePayload,
      editorMessage = '',
      copilotAcceptedMessage = ''
    ) {
      try {
        await this.$store.dispatch(
          'createPendingMessageAndSend',
          messagePayload
        );
        emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
        emitter.emit(BUS_EVENTS.MESSAGE_SENT);
        this.removeFromDraft();
        this.sendMessageAnalyticsData(messagePayload.private, {
          editorMessage,
          copilotAcceptedMessage,
        });
        return true;
      } catch (error) {
        const rawError = error?.response?.data?.error;
        const errorMessage =
          (typeof rawError === 'string' && rawError) ||
          this.$t('CONVERSATION.MESSAGE_ERROR');
        useAlert(errorMessage);
        return false;
      }
    },
    async onSendWhatsAppReply(messagePayload) {
      this.sendMessage({
        conversationId: this.currentChat.id,
        ...messagePayload,
      });
      this.hideWhatsappTemplatesModal();
    },
    async onSendContentTemplateReply(messagePayload) {
      this.sendMessage({
        conversationId: this.currentChat.id,
        ...messagePayload,
      });
      this.hideContentTemplatesModal();
    },
    // Aceleres: handler do item "Notas da conversa" do menu "+" no
    // ReplyBottomPanel — alterna entre Reply e Note.
    onToggleNoteMode() {
      this.setReplyMode(
        this.isOnPrivateNote
          ? REPLY_EDITOR_MODES.REPLY
          : REPLY_EDITOR_MODES.NOTE
      );
    },
    setReplyMode(mode = REPLY_EDITOR_MODES.REPLY) {
      // Clear attachments when switching between private note and reply modes
      // This is to prevent from breaking the upload rules
      if (this.attachedFiles.length > 0) this.attachedFiles = [];

      const { can_reply: canReply } = this.currentChat;
      this.$store.dispatch('draftMessages/setReplyEditorMode', {
        mode,
      });
      if (canReply || this.isAWhatsAppChannel || this.isAPIInbox)
        this.replyType = mode;
      if (this.isRecordingAudio) {
        this.toggleAudioRecorder();
      }
    },
    clearEditorSelection() {
      this.updateEditorSelectionWith = '';
    },
    addIntoEditor(content) {
      this.updateEditorSelectionWith = content;
      this.onFocus();
    },
    executeCopilotAction(action, data) {
      this.copilot.execute(action, data);
    },
    clearMessage() {
      this.message = '';
      this.clearCopilotAcceptedMessage();
      if (this.sendWithSignature && !this.isPrivate) {
        // if signature is enabled, append it to the message
        const effectiveChannelType = getEffectiveChannelType(
          this.channelType,
          this.inbox?.medium || ''
        );
        this.message = appendSignature(
          this.message,
          this.messageSignature,
          effectiveChannelType
        );
      }
      this.attachedFiles = [];
      this.isRecordingAudio = false;
      this.resetReplyToMessage();
      this.resetAudioRecorderInput();
    },
    clearEmailField() {
      this.ccEmails = '';
      this.bccEmails = '';
      this.toEmails = '';
    },

    getEmojiPickerTrigger() {
      return this.replyEditor?.querySelector('.acl-composer-emoji');
    },
    refreshEmojiPickerPosition() {
      if (this.showEmojiPicker) recomputeAllPoppers();
    },
    toggleEmojiPicker() {
      const shouldOpen = !this.showEmojiPicker;
      if (shouldOpen) {
        emitter.emit(BUS_EVENTS.EMOJI_PICKER_OPENED, { owner: 'reply' });
      }
      this.showEmojiPicker = shouldOpen;
      this.suppressWhatsAppGallery = shouldOpen;
      if (shouldOpen) {
        this.$nextTick(this.refreshEmojiPickerPosition);
      }
    },
    openMediaPicker() {
      this.showEmojiPicker = false;
      this.suppressWhatsAppGallery = false;
    },
    prepareMediaUpload(file) {
      this.openMediaPicker();
      return this.onFileUpload(file);
    },
    aceleresApiBase() {
      return window.location?.host === 'atendimento.zoraxy.ai'
        ? 'https://homolog.aceleres.ai'
        : 'https://aceleres.ai';
    },
    gifConversationPayload() {
      const chat = this.currentChat || {};
      const sender = chat.meta?.sender || {};
      const attributeSources = [
        chat.additional_attributes,
        chat.custom_attributes,
        sender.additional_attributes,
        sender.custom_attributes,
      ];
      const remoteJid =
        attributeSources
          .map(attributes => attributes?.remote_jid || attributes?.remoteJid)
          .find(Boolean) ||
        sender.identifier ||
        sender.phone_number ||
        '';

      return {
        chatwoot_account_id: parseInt(
          this.$store.getters.getCurrentAccountId,
          10
        ),
        chatwoot_conversation_id: parseInt(
          chat.id ||
            this.$route?.params?.conversationId ||
            this.$route?.params?.conversation_id ||
            this.$route?.params?.id,
          10
        ),
        chatwoot_inbox_id:
          parseInt(chat.inbox_id || this.inbox?.id, 10) || null,
        // É apenas uma dica para compatibilidade. A API resolve o JID
        // canônico no servidor a partir da conversa autorizada.
        remote_jid: remoteJid ? String(remoteJid) : null,
      };
    },
    async fetchGifRequest(url, options, timeoutMessage) {
      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), 45000);

      try {
        return await fetch(url, { ...options, signal: controller.signal });
      } catch (error) {
        if (error?.name === 'AbortError') {
          throw new Error(timeoutMessage);
        }

        throw error;
      } finally {
        window.clearTimeout(timeoutId);
      }
    },
    async onGifSelect(gif) {
      if (this.isSendingGif) {
        useAlert('Uma figurinha já está sendo enviada. Aguarde a conclusão.');
        return false;
      }

      // Fecha o picker antes de iniciar a leitura do arquivo.
      this.isSendingGif = true;
      this.showEmojiPicker = false;
      try {
        if (!gif || !this.currentChat?.id) {
          useAlert('Não foi possível identificar a conversa.');
          return false;
        }
        const file = await this.stickerFileFromLibrary(gif);
        const sent = await this.sendStickerAsChatwootMessage(
          file,
          String(this.message || '').trim()
        );
        if (sent) {
          this.message = '';
        }
        return sent;
      } catch (error) {
        useAlert(
          error?.message || 'Erro ao enviar figurinha. Tente novamente.'
        );
        return false;
      } finally {
        this.isSendingGif = false;
      }
    },
    stickerBinaryUrl(gif) {
      const id = gif?.id;
      return id ? `${this.aceleresApiBase()}/api/stickers/${id}/binary` : '';
    },
    async stickerFileFromLibrary(gif) {
      const url = this.stickerBinaryUrl(gif);
      if (!url) {
        throw new Error(
          'Não foi possível identificar a figurinha selecionada.'
        );
      }
      // O arquivo também é servido por /storage, mas aquele caminho sai pelo
      // nginx sem cabeçalho CORS — e aqui é `fetch`, não `<img>`.
      const response = await this.fetchGifRequest(
        url,
        {
          headers: {
            'X-Chatwoot-Token': this.currentUser?.access_token || '',
          },
        },
        'A leitura da figurinha demorou demais. Tente novamente.'
      );
      if (!response.ok) {
        throw new Error('Não foi possível ler a figurinha selecionada.');
      }
      const blob = await response.blob();
      const type = blob.type || 'image/gif';
      const extension = (type.split('/')[1] || 'gif').replace(
        /[^a-z0-9]/gi,
        ''
      );
      const baseName = String(gif?.title || 'figurinha').replace(
        /[^\w-]+/g,
        '-'
      );
      return new File([blob], `${baseName}.${extension}`, { type });
    },
    /**
     * A figurinha passa pelo compositor do próprio Chatwoot: o balão aparece
     * na hora pelo render otimista, a autoria é a nativa (sem depender de
     * `wame_author_chatwoot_user_id`) e o eco da WAME é descartado por
     * `SyncWameMessageToChatwoot::isOutboundEcho`. O marcador
     * `aceleres_sticker` faz o Laravel usar o endpoint de sticker no lugar do
     * de imagem, e `wame_media_type` preserva o visual de figurinha na bolha.
     */
    async sendStickerAsChatwootMessage(file, caption) {
      return this.sendMessage({
        conversationId: this.currentChat.id,
        message: caption || '',
        private: false,
        sender: this.sender,
        files: [file],
        contentAttributes: {
          aceleres_sticker: true,
          wame_media_type: 'sticker',
        },
      });
    },
    isGifAttachment(attachment) {
      const file = attachment?.resource?.file;
      const type = file?.type || attachment?.resource?.content_type || '';
      const name = file?.name || attachment?.resource?.filename || '';
      return type.toLowerCase() === 'image/gif' || /\.gif$/i.test(name);
    },
    async gifFileFromAttachment(attachment) {
      const originalFile = attachment?.resource?.file;
      if (originalFile instanceof File) return originalFile;

      if (!attachment?.thumb) {
        throw new Error('Não foi possível ler o GIF-fonte da figurinha.');
      }
      const response = await fetch(attachment.thumb);
      if (!response.ok)
        throw new Error('Não foi possível ler o GIF-fonte da figurinha.');
      const blob = await response.blob();
      const name = attachment?.resource?.filename || 'gif-anexado.gif';
      return new File([blob], name, { type: 'image/gif' });
    },
    async sendAttachedGifsDirectly() {
      const context = this.gifConversationPayload();
      if (!context.chatwoot_account_id || !context.chatwoot_conversation_id) {
        useAlert('Não foi possível identificar a conversa.');
        return false;
      }

      this.isSendingAttachments = true;
      this.attachmentSendProgress = {
        current: 0,
        total: this.attachedFiles.length,
        active: 1,
      };
      try {
        for (let index = 0; index < this.attachedFiles.length; index += 1) {
          const attachment = this.attachedFiles[index];
          const file = await this.gifFileFromAttachment(attachment);
          const formData = new FormData();
          formData.append('gif', file);
          formData.append('scope', 'user');
          formData.append('name', file.name.replace(/\.gif$/i, ''));
          formData.append(
            'chatwoot_account_id',
            String(context.chatwoot_account_id)
          );
          formData.append(
            'chatwoot_conversation_id',
            String(context.chatwoot_conversation_id)
          );
          if (context.chatwoot_inbox_id) {
            formData.append(
              'chatwoot_inbox_id',
              String(context.chatwoot_inbox_id)
            );
          }
          // Os uploads são seriais para preservar a legenda na primeira figurinha.
          // eslint-disable-next-line no-await-in-loop
          const upload = await this.fetchGifRequest(
            `${this.aceleresApiBase()}/api/stickers`,
            {
              method: 'POST',
              headers: {
                Accept: 'application/json',
                'X-Chatwoot-Token': this.currentUser?.access_token || '',
              },
              body: formData,
            },
            'O upload da figurinha demorou demais. Tente novamente.'
          );
          const uploaded = await upload.json().catch(() => ({}));
          if (
            !upload.ok ||
            uploaded?.success === false ||
            !uploaded?.data?.id
          ) {
            throw new Error(
              uploaded?.message || 'Não foi possível preparar a figurinha.'
            );
          }

          // O upload acima mantém a figurinha na biblioteca do atendente; o
          // envio em si vai pelo compositor do Chatwoot, com o arquivo local
          // que já está em mãos.
          // eslint-disable-next-line no-await-in-loop
          const sent = await this.sendStickerAsChatwootMessage(
            file,
            String(
              attachment.caption || (index === 0 ? this.message : '') || ''
            ).trim()
          );
          if (!sent) {
            throw new Error('Não foi possível enviar a figurinha.');
          }
          this.attachmentSendProgress.current = index + 1;
          this.attachmentSendProgress.active = index + 1;
        }
        this.attachedFiles = [];
        this.message = '';
        return true;
      } catch (error) {
        useAlert(
          error?.message || 'Não foi possível enviar a figurinha ao WhatsApp.'
        );
        return false;
      } finally {
        this.isSendingAttachments = false;
        this.attachmentSendProgress = { current: 0, total: 0, active: 0 };
      }
    },
    toggleAudioRecorder() {
      this.isRecordingAudio = !this.isRecordingAudio;
      if (!this.isRecordingAudio) {
        this.resetAudioRecorderInput();
      }
    },
    toggleAudioRecorderPlayPause() {
      if (!this.$refs.audioRecorderInput) return;
      // Áudio já finalizado → controla playback (ouvir antes de enviar)
      if (this.hasRecordedAudio) {
        this.$refs.audioRecorderInput.playPause();
        return;
      }
      // Gravando: alterna PAUSA/CONTINUA sem finalizar nem anexar
      if (this.isRecordingPaused) {
        this.$refs.audioRecorderInput.resumeRecording();
        this.isRecordingPaused = false;
        this.recordingAudioState = '';
      } else {
        this.$refs.audioRecorderInput.pauseRecording();
        this.isRecordingPaused = true;
        this.recordingAudioState = 'recording-paused';
      }
    },
    // Finaliza a gravação e anexa (NÃO envia — fica pronto pra revisar/enviar)
    finishAudioRecording() {
      if (!this.$refs.audioRecorderInput) return;
      this.$refs.audioRecorderInput.stopRecording();
      this.isRecordingPaused = false;
    },
    // Aceleres 2026-07-03: botão Enviar durante a gravação (WhatsApp) —
    // finaliza e envia em seguida. Seta a flag; o watcher em attachedFiles
    // chama onSendReply assim que o áudio finalizado entra na bandeja (o
    // attach via finishRecord/onFileUpload é assíncrono).
    sendAudioRecording() {
      if (!this.$refs.audioRecorderInput) return;
      this.sendOnFinish = true;
      this.$refs.audioRecorderInput.stopRecording();
      this.isRecordingPaused = false;
    },
    // Apaga a gravação atual sem anexar nem enviar
    deleteAudioRecording() {
      this.isRecordingPaused = false;
      this.resetAudioRecorderInput();
    },
    // Aceleres: microfone inacessível (permissão negada / iframe / WebView).
    // Mostra o motivo e reseta o estado de gravação p/ o composer voltar ao normal.
    onRecordError(message) {
      useAlert(message || 'Não foi possível acessar o microfone.');
      this.resetAudioRecorderInput();
    },
    hideEmojiPicker() {
      if (this.showEmojiPicker) {
        this.toggleEmojiPicker();
      } else {
        this.suppressWhatsAppGallery = false;
      }
    },
    onEmojiPickerOpened({ owner } = {}) {
      if (owner === 'reply') return;
      this.showEmojiPicker = false;
      this.suppressWhatsAppGallery = false;
    },
    onTypingOn() {
      this.toggleTyping('on');
    },
    onTypingOff() {
      this.toggleTyping('off');
    },
    onBlur() {
      this.isFocused = false;
      this.saveDraft(this.conversationIdByRoute, this.replyType);
    },
    onFocus() {
      this.isFocused = true;
    },
    onRecordProgressChanged(duration) {
      this.recordingAudioDurationText = duration;
    },
    onFinishRecorder(file) {
      this.recordingAudioState = 'stopped';
      this.hasRecordedAudio = true;
      // Added a new key isRecordedAudio to the file to find it's and recorded audio
      // Because to filter and show only non recorded audio and other attachments
      const autoRecordedFile = {
        ...file,
        isRecordedAudio: true,
      };
      const result = file && this.onFileUpload(autoRecordedFile);
      // Aceleres: desmonta a faixa de gravação (o áudio anexado passa a ser
      // preview/enviado pela bandeja de anexos — fluxo "melhoria leve"
      // estilo ChatGPT, sem takeover do composer).
      this.isRecordingAudio = false;
      return result;
    },
    toggleTyping(status) {
      const conversationId = this.currentChat.id;
      const isPrivate = this.isPrivate;

      if (!conversationId) {
        return;
      }

      this.$store.dispatch('conversationTypingStatus/toggleTyping', {
        status,
        conversationId,
        isPrivate,
      });
    },
    attachFile({ blob, file }) {
      const sourceFile = file?.file || file;
      if (!sourceFile) return;

      const reader = new FileReader();
      reader.readAsDataURL(sourceFile);
      reader.onloadend = () => {
        this.releaseAttachmentUploadSlot();
        const isFirstFile = !this.attachedFiles.some(f => !f?.isRecordedAudio);
        let initialCaption = '';
        if (
          isFirstFile &&
          !this.isPrivate &&
          !file?.isRecordedAudio &&
          this.message &&
          this.message.trim()
        ) {
          // O editor fica oculto ao anexar o primeiro arquivo público.
          // Mantém o rascunho na legenda da prévia para não perdê-lo.
          initialCaption = this.message;
          this.message = '';
        }
        const previewId = isPdfFile(sourceFile)
          ? `pdf-preview-${Date.now()}-${Math.random()}`
          : null;
        this.attachedFiles.push({
          currentChatId: this.currentChat.id,
          resource: blob || file,
          isPrivate: this.isPrivate,
          thumb: reader.result,
          originalFileUrl: previewId ? reader.result : null,
          previewId,
          isPdfPreview: Boolean(previewId),
          isPdfPreviewLoading: Boolean(previewId),
          blobSignedId: blob ? blob.signed_id : undefined,
          isRecordedAudio: file?.isRecordedAudio || false,
          caption: initialCaption,
        });
        if (previewId) {
          this.loadPdfFirstPagePreview(sourceFile, previewId);
        }
        // Aceleres 2026-07-03 (fix): botão Enviar da gravação (WhatsApp).
        // sendAudioRecording setou sendOnFinish antes do stopRecording; a
        // cadeia convertAudio → finishRecord → onFileUpload termina AQUI,
        // logo após o arquivo cair na bandeja. Disparamos o envio neste ponto
        // (e não num watcher em attachedFiles, que em Vue 3 não dispara no
        // push — ver watch{}). O blob direto já foi upado neste momento.
        if (this.sendOnFinish) {
          this.sendOnFinish = false;
          this.$nextTick(() => this.onSendReply());
        }
      };
    },
    async loadPdfFirstPagePreview(file, previewId) {
      try {
        const previewResult = await createPdfFirstPagePreview(file);
        if (!previewResult?.preview || this.$?.isUnmounted) return;

        const attachmentIndex = this.attachedFiles.findIndex(
          attachment => attachment.previewId === previewId
        );
        if (attachmentIndex < 0) return;

        const attachment = this.attachedFiles[attachmentIndex];
        this.attachedFiles.splice(attachmentIndex, 1, {
          ...attachment,
          thumb: previewResult.preview,
          pdfPageCount: previewResult.pageCount,
          isPdfPreviewLoading: false,
        });
      } catch {
        if (this.$?.isUnmounted) return;

        const attachmentIndex = this.attachedFiles.findIndex(
          attachment => attachment.previewId === previewId
        );
        if (attachmentIndex < 0) return;

        const attachment = this.attachedFiles[attachmentIndex];
        this.attachedFiles.splice(attachmentIndex, 1, {
          ...attachment,
          isPdfPreviewLoading: false,
        });
      }
    },
    removeAttachment(attachments) {
      this.attachedFiles = attachments;
    },
    handleCannedAttachment(payload) {
      if (!payload.attachmentUrl || !payload.attachmentSignedId) return;
      const urlFileName = payload.attachmentUrl.split('?')[0].split('/').pop();
      const fname =
        payload.fileName || decodeURIComponent(urlFileName) || 'attachment';
      const ext = fname.includes('.')
        ? fname.split('.').pop().toLowerCase()
        : '';
      const imgExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'];
      const videoExts = ['mp4', 'webm', 'mov', 'm4v'];
      const audExts = ['mp3', 'wav', 'ogg', 'm4a', 'aac'];
      let mime = payload.fileType || 'application/octet-stream';
      if (!payload.fileType && imgExts.includes(ext)) {
        mime = 'image/' + ext;
      } else if (!payload.fileType && videoExts.includes(ext)) {
        mime = 'video/' + ext;
      } else if (!payload.fileType && audExts.includes(ext)) {
        mime = 'audio/' + ext;
      }
      this.attachedFiles.push({
        currentChatId: this.currentChat.id,
        resource: {
          content_type: mime,
          filename: fname,
          byte_size: Number(payload.fileSize) || 0,
        },
        isPrivate: this.isOnPrivateNote,
        thumb: payload.attachmentUrl,
        blobSignedId: payload.attachmentSignedId,
        isRecordedAudio: false,
        isCannedResponseAttachment: true,
        caption: payload.caption || '',
      });
    },
    updateAttachmentCaption({ index, caption }) {
      const nonRecorded = this.attachedFiles.filter(f => !f?.isRecordedAudio);
      const target = nonRecorded[index];
      if (!target) return;
      const realIndex = this.attachedFiles.indexOf(target);
      if (realIndex < 0) return;
      const updated = { ...this.attachedFiles[realIndex], caption };
      this.attachedFiles.splice(realIndex, 1, updated);
    },
    // Resolve what to hand the backend for an attached file.
    // - Direct uploads on: the ActiveStorage blob signed id.
    // - Pre-uploaded attachments (e.g. canned responses) carry a blobSignedId but no
    //   raw File in resource; MessageBuilder accepts the signed id directly, so reuse it
    //   even when direct uploads are off (otherwise the file would be undefined and the
    //   attachment never reaches the customer).
    // - Otherwise: the raw File object for multipart upload.
    getAttachableFile(attachment) {
      if (this.globalConfig.directUploadsEnabled) {
        return attachment.blobSignedId;
      }
      if (attachment.blobSignedId && !attachment.resource?.file) {
        return attachment.blobSignedId;
      }
      return attachment.resource.file;
    },
    setReplyToInPayload(payload) {
      if (this.inReplyTo?.id) {
        return {
          ...payload,
          contentAttributes: {
            ...payload.contentAttributes,
            in_reply_to: this.inReplyTo.id,
          },
        };
      }

      return payload;
    },
    getMultipleMessagesPayload(message) {
      const multipleMessagePayload = [];

      if (this.attachedFiles && this.attachedFiles.length) {
        const usesMediaGallery =
          (this.usesMediaGallery ||
            this.isOnWhatsApp ||
            this.hasCannedResponseAttachments) &&
          !this.isPrivate;
        let fallbackCaption =
          this.isAnInstagramChannel || this.isATiktokChannel ? '' : message;
        this.attachedFiles.forEach(attachment => {
          const attachedFile = this.getAttachableFile(attachment);
          let perAttachmentCaption;
          if (usesMediaGallery) {
            // Cada arquivo usa a própria legenda, editada na prévia.
            perAttachmentCaption = attachment.caption || '';
          } else {
            perAttachmentCaption = fallbackCaption;
          }
          let attachmentPayload = {
            conversationId: this.currentChat.id,
            files: [attachedFile],
            private: false,
            message: perAttachmentCaption,
            sender: this.sender,
          };

          attachmentPayload = this.setReplyToInPayload(attachmentPayload);
          multipleMessagePayload.push(attachmentPayload);
          // Legacy path (non-WhatsApp): only the first attachment gets the caption
          if (!usesMediaGallery && !this.isAnInstagramChannel) {
            fallbackCaption = '';
          }
        });
      }

      const hasNoAttachments =
        !this.attachedFiles || !this.attachedFiles.length;
      // For Instagram and TikTok, text must always be sent as a separate message (no captions on attachments).
      // For WhatsApp, we only need a text message if there are no attachments.
      if (
        ((this.isAnInstagramChannel || this.isATiktokChannel) &&
          this.message) ||
        (!(this.isAnInstagramChannel || this.isATiktokChannel) &&
          hasNoAttachments)
      ) {
        let messagePayload = {
          conversationId: this.currentChat.id,
          message,
          private: false,
          sender: this.sender,
        };

        messagePayload = this.setReplyToInPayload(messagePayload);

        multipleMessagePayload.push(messagePayload);
      }

      return multipleMessagePayload;
    },
    getMessagePayload(message) {
      const messageWithQuote = this.getMessageWithQuotedEmailText(message);

      let messagePayload = {
        conversationId: this.currentChat.id,
        message: messageWithQuote,
        private: this.isPrivate,
        sender: this.sender,
      };
      messagePayload = this.setReplyToInPayload(messagePayload);

      if (this.attachedFiles && this.attachedFiles.length) {
        messagePayload.files = [];
        this.attachedFiles.forEach(attachment => {
          messagePayload.files.push(this.getAttachableFile(attachment));
        });
      }

      if (this.ccEmails && !this.isOnPrivateNote) {
        messagePayload.ccEmails = this.ccEmails;
      }

      if (this.bccEmails && !this.isOnPrivateNote) {
        messagePayload.bccEmails = this.bccEmails;
      }

      if (this.toEmails && !this.isOnPrivateNote) {
        messagePayload.toEmails = this.toEmails;
      }
      return messagePayload;
    },
    setCcEmails(value) {
      this.bccEmails = value.bccEmails;
      this.ccEmails = value.ccEmails;
    },
    setCCAndToEmailsFromLastChat() {
      const conversationContact = this.currentChat?.meta?.sender?.email || '';
      const { email: inboxEmail, forward_to_email: forwardToEmail } =
        this.inbox;

      const { cc, bcc, to } = getRecipients(
        this.lastEmail,
        conversationContact,
        inboxEmail,
        forwardToEmail
      );

      this.toEmails = to.join(', ');
      this.ccEmails = cc.join(', ');
      this.bccEmails = bcc.join(', ');
    },
    fetchAndSetReplyTo() {
      const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
      const replyToMessageId = LocalStorage.getFromJsonStore(
        replyStorageKey,
        this.conversationId
      );

      this.inReplyTo = this.currentChat?.messages?.find(message => {
        if (message.id === replyToMessageId) {
          return true;
        }
        return false;
      });
    },
    onReplyToMessage() {
      this.fetchAndSetReplyTo();
      if (this.inReplyTo) {
        this.$nextTick(() => {
          const pos = this.isSignatureEnabledForInbox ? 'start' : 'end';
          this.messageEditor?.focusEditorInputField(pos);
        });
      }
    },
    resetReplyToMessage() {
      const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
      LocalStorage.deleteFromJsonStore(replyStorageKey, this.conversationId);
      emitter.emit(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE);
    },
    onNewConversationModalActive(isActive) {
      // Issue is if the new conversation modal is open and we drag and drop the file
      // then the file is not getting attached to the new conversation modal
      // and it is getting attached to the current conversation reply box
      // so to fix this we are removing the drag and drop event listener from the current conversation reply box
      // When new conversation modal is open
      this.newConversationModalActive = isActive;
    },
    onSearchPopoverClose() {
      this.showArticleSearchPopover = false;
    },
    toggleInsertArticle() {
      this.showArticleSearchPopover = !this.showArticleSearchPopover;
    },
    resetAudioRecorderInput() {
      this.recordingAudioDurationText = '00:00';
      this.isRecordingAudio = false;
      this.recordingAudioState = '';
      this.isRecordingPaused = false;
      this.hasRecordedAudio = false;
      // Aceleres 2026-07-03 (fix): limpa a flag de envio-pós-gravação nos
      // caminhos de aborto (erro no mic/convertAudio, descartar, toggle off).
      // Sem isso, uma falha no finish deixaria sendOnFinish=true e o próximo
      // anexo normal seria enviado automaticamente pelo gatilho em attachFile.
      this.sendOnFinish = false;
      // Only clear the recorded audio when we click toggle button.
      this.attachedFiles = this.attachedFiles.filter(
        file => !file?.isRecordedAudio
      );
    },
    toggleEditorSize() {
      this.$emit('toggleEditorSize');
      this.$nextTick(() => this.messageEditor?.focusEditorInputField());
    },
    onSubmitCopilotReply() {
      const acceptedMessage = this.copilot.accept();
      this.message = acceptedMessage;
      this.setCopilotAcceptedMessage(acceptedMessage);
    },
  },
};
</script>

<template>
  <ReplyBoxBanner :message="message" :is-on-private-note="isOnPrivateNote" />
  <Teleport
    v-if="isConversationFileDragActive"
    to=".conversation-details-wrap.chat-whats"
  >
    <div
      class="acl-conversation-drop-overlay acl-conversation-drop-overlay--persistent"
      role="status"
      aria-live="polite"
    >
      <div class="acl-conversation-drop-overlay__frame">
        <span
          class="acl-conversation-drop-overlay__icon i-lucide-cloud-upload"
          aria-hidden="true"
        />
        <h4 class="acl-conversation-drop-overlay__title">
          {{ $t('CONVERSATION.REPLYBOX.DRAG_DROP') }}
        </h4>
      </div>
    </div>
  </Teleport>

  <!-- Aceleres: painel "Sessão expirada" (janela 24h da API Oficial).
       Renderiza-se sozinho quando inbox é API Oficial travada. -->
  <SessionExpiredPanel v-if="showSessionExpiryPanels" />
  <AceleresWebhookSessionExpiredPanel v-if="showSessionExpiryPanels" />
  <AceleresWhatsappDisconnectedPanel
    v-if="whatsappInstanceDisconnected && !isOnPrivateNote"
    :inbox="inbox"
  />
  <!-- Aceleres: ReplyTopPanel movido pra FORA da .reply-box (pedido
       2026-07-01). Agora a pílula do reply-box só contém o editor +
       ícones. O top panel (reply/note toggle + character warning) fica
       como faixa informativa ACIMA da pílula. -->
  <ReplyTopPanel
    v-if="false"
    class="acl-reply-top-out mx-2"
    :mode="replyType"
    :conversation-id="conversationId"
    :is-reply-restricted="isReplyRestricted"
    :disabled="
      (copilot.isActive.value && copilot.isButtonDisabled.value) ||
      showAudioRecorderEditor
    "
    :is-editor-disabled="isEditorDisabled"
    :is-message-length-reaching-threshold="isMessageLengthReachingThreshold"
    :characters-remaining="charactersRemaining"
    :editor-content="message"
    @set-reply-mode="setReplyMode"
    @toggle-editor-size="toggleEditorSize"
    @toggle-copilot="copilot.toggleEditor"
    @execute-copilot-action="executeCopilotAction"
  />
  <!-- Aceleres: banners informacionais movidos pra FORA da .reply-box
       (pedido 2026-07-01) — reply-to preview, quoted email, attachments
       e signature alert viram cards acima da pílula em vez de morar
       dentro do input. Assim a pílula fica exclusiva pra:
       [+] [emoji] [placeholder text] [🎤/enviar]. -->
  <div v-if="shouldShowReplyToMessage" class="acl-reply-banners mx-2 mb-1">
    <ReplyToMessage :message="inReplyTo" @dismiss="resetReplyToMessage" />
  </div>
  <QuotedEmailPreview
    v-if="shouldShowQuotedPreview && isDefaultEditorMode"
    :quoted-email-text="quotedEmailText"
    :preview-text="quotedEmailPreviewText"
    class="acl-reply-banners mx-2 mb-1"
    @toggle="toggleQuotedReply"
  />
  <div
    v-if="hasAttachments && isDefaultEditorMode"
    class="acl-reply-banners mx-2 mb-1 bg-transparent py-0"
    @paste="onPaste"
  >
    <AttachmentPreview
      :attachments="attachedFiles"
      :conversation="currentChat"
      :contact="currentContact"
      :mode="showWhatsAppGallery ? 'gallery' : 'compact'"
      :enable-multiple-file-upload="enableMultipleFileUpload"
      :on-add-more="prepareMediaUpload"
      :is-sending="isSendingAttachments"
      :send-progress="attachmentSendProgress"
      @remove-attachment="removeAttachment"
      @update-caption="updateAttachmentCaption"
      @send="onSendReply"
    />
  </div>
  <div
    v-show="!showWhatsAppGallery"
    ref="replyEditor"
    class="reply-box"
    :class="[
      replyBoxClass,
      { 'reply-box--gallery-hidden': showWhatsAppGallery },
    ]"
  >
    <div v-if="isOnPrivateNote" class="acl-internal-note-banner">
      <span class="i-ph-note-pencil size-4" aria-hidden="true" />
      <strong>{{ $t('CONVERSATION.REPLYBOX.INTERNAL_NOTE_TITLE') }}</strong>
      <span class="acl-internal-note-team-only">
        {{ $t('CONVERSATION.REPLYBOX.TEAM_ONLY') }}
      </span>
      <button
        v-if="!isReplyRestricted"
        type="button"
        class="ml-auto inline-flex size-10 min-h-10 min-w-10 shrink-0 items-center justify-center rounded-full text-n-amber-11 transition-colors hover:bg-n-amber-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-amber-8"
        :aria-label="$t('CONVERSATION.REPLYBOX.BACK_TO_REPLY')"
        @click="onToggleNoteMode"
      >
        <span class="i-lucide-x size-5" />
      </button>
    </div>
    <ArticleSearchPopover
      v-if="showArticleSearchPopover && connectedPortalSlug"
      :selected-portal-slug="connectedPortalSlug"
      @insert="handleInsert"
      @close="onSearchPopoverClose"
    />
    <Transition
      mode="out-in"
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0 translate-y-2 scale-[0.98]"
      enter-to-class="opacity-100 translate-y-0 scale-100"
      leave-active-class="transition-all duration-200 ease-in"
      leave-from-class="opacity-100 translate-y-0 scale-100"
      leave-to-class="opacity-0 translate-y-2 scale-[0.98]"
    >
      <div :key="copilot.editorTransitionKey.value" class="reply-box__top">
        <!-- Aceleres: ReplyToMessage removido daqui — agora é banner
             acima do .reply-box (via .acl-reply-banners). -->
        <!-- Aceleres: o picker usa o posicionador do Floating Vue para
             permanecer ancorado ao botão de emoji do compositor. -->
        <Dropdown
          v-if="showEmojiPicker"
          :shown="showEmojiPicker"
          :triggers="[]"
          :popper-triggers="[]"
          :reference-node="getEmojiPickerTrigger"
          placement="top"
          :distance="8"
          :overflow-padding="8"
          flip
          shift
          shift-cross-axis
          strategy="fixed"
          handle-resize
          :auto-hide="false"
          popper-class="acl-composer-emoji-popper"
        >
          <span class="absolute size-0 overflow-hidden" aria-hidden="true" />
          <template #popper>
            <div
              v-on-clickaway="hideEmojiPicker"
              class="aceleres-emoji-picker-card acl-inline-picker"
              @click.stop
            >
              <div class="aceleres-emoji-picker-body">
                <EmojiInput
                  embedded
                  show-recent-reactions
                  :show-search-icon="false"
                  :on-click="addIntoEditor"
                  :on-gif-select="onGifSelect"
                  scroll-sync-tabs
                />
              </div>
            </div>
          </template>
        </Dropdown>
        <ReplyEmailHead
          v-if="showReplyHead && isDefaultEditorMode"
          v-model:cc-emails="ccEmails"
          v-model:bcc-emails="bccEmails"
          v-model:to-emails="toEmails"
        />
        <AudioRecorder
          v-if="showAudioRecorderEditor"
          ref="audioRecorderInput"
          :audio-record-format="audioRecordFormat"
          variant="conversation"
          :is-paused="isRecordingPaused"
          @recorder-progress-changed="onRecordProgressChanged"
          @finish-record="onFinishRecorder"
          @play="recordingAudioState = 'playing'"
          @pause="recordingAudioState = 'paused'"
          @record-error="onRecordError"
          @delete="deleteAudioRecording"
          @playpause="toggleAudioRecorderPlayPause"
          @send="sendAudioRecording"
        />
        <CopilotEditorSection
          v-if="copilot.isActive.value && !showAudioRecorderEditor"
          :show-copilot-editor="copilot.showEditor.value"
          :is-generating-content="copilot.isGenerating.value"
          :generated-content="copilot.generatedContent.value"
          :placeholder="$t('CONVERSATION.FOOTER.COPILOT_MSG_INPUT')"
          @focus="onFocus"
          @blur="onBlur"
          @clear-selection="clearEditorSelection"
          @close="copilot.reset"
          @content-ready="copilot.setContentReady"
          @send="copilot.sendFollowUp"
        />
        <WootMessageEditor
          v-else-if="!showWhatsAppGallery && !showAudioRecorderEditor"
          ref="messageEditor"
          v-model="message"
          :conversation-id="conversationId"
          :editor-id="editorStateId"
          class="input popover-prosemirror-menu"
          :is-private="isOnPrivateNote"
          :placeholder="messagePlaceHolder"
          :update-selection-with="updateEditorSelectionWith"
          :min-height="4"
          :disabled="isEditorDisabled"
          enable-variables
          :variables="messageVariables"
          :signature="messageSignature"
          allow-signature
          :channel-type="channelType"
          :medium="inbox.medium"
          @typing-off="onTypingOff"
          @typing-on="onTypingOn"
          @focus="onFocus"
          @blur="onBlur"
          @toggle-user-mention="toggleUserMention"
          @toggle-canned-menu="toggleCannedMenu"
          @toggle-variables-menu="toggleVariablesMenu"
          @clear-selection="clearEditorSelection"
          @execute-copilot-action="executeCopilotAction"
          @canned-attachment="handleCannedAttachment"
          @keydown="onEditorKeydown"
          @send="onEditorSend"
        />

        <!-- Aceleres: QuotedEmailPreview e AttachmentPreview ficam acima do
             .reply-box para manter o composer limpo. -->
      </div>
    </Transition>

    <Transition
      mode="out-in"
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0 translate-y-2 scale-[0.98]"
      enter-to-class="opacity-100 translate-y-0 scale-100"
      leave-active-class="transition-all duration-200 ease-in"
      leave-from-class="opacity-100 translate-y-0 scale-100"
      leave-to-class="opacity-0 translate-y-2 scale-[0.98]"
    >
      <div
        v-if="aceleresNeedsStartGate"
        key="aceleres-start-gate"
        class="aceleres-start-gate col-start-1 row-start-1 flex items-center justify-center p-1"
      >
        <button
          type="button"
          :disabled="aceleresStarting"
          class="aceleres-start-btn inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#00a884] text-white text-sm font-semibold hover:bg-[#008f6f] disabled:opacity-60 transition"
          @click="aceleresStartConversation"
        >
          <span class="i-lucide-play size-4" />
          {{ aceleresStarting ? 'Iniciando…' : 'Iniciar atendimento' }}
        </button>
      </div>
      <CopilotReplyBottomPanel
        v-else-if="copilot.isActive.value"
        key="copilot-bottom-panel"
        :is-generating-content="copilot.isButtonDisabled.value"
        @submit="onSubmitCopilotReply"
        @cancel="copilot.reset"
      />
      <ReplyBottomPanel
        v-else-if="!showAudioRecorderEditor && !showWhatsAppGallery"
        key="reply-bottom-panel"
        :conversation-id="conversationId"
        :enable-multiple-file-upload="enableMultipleFileUpload"
        :enable-whats-app-templates="showWhatsappTemplates"
        :enable-content-templates="showContentTemplates"
        :inbox="inbox"
        :is-on-private-note="isOnPrivateNote"
        :is-recording-audio="isRecordingAudio"
        :is-send-disabled="isReplyButtonDisabled"
        :is-note="isPrivate"
        :is-editor-disabled="isEditorDisabled"
        :on-file-upload="prepareMediaUpload"
        :on-open-media="openMediaPicker"
        :on-send="onSendReply"
        :conversation-type="conversationType"
        :recording-audio-duration-text="recordingAudioDurationText"
        :recording-audio-state="recordingAudioState"
        :send-button-text="replyButtonLabel"
        :show-copilot-button="!copilot.isActive.value"
        :show-audio-recorder="showAudioRecorder"
        :show-emoji-picker="showEmojiPicker"
        :show-file-upload="showFileUpload"
        :show-quoted-reply-toggle="shouldShowQuotedReplyToggle"
        :quoted-reply-enabled="quotedReplyPreference"
        :toggle-audio-recorder-play-pause="toggleAudioRecorderPlayPause"
        :toggle-audio-recorder="toggleAudioRecorder"
        :finish-audio-recording="finishAudioRecording"
        :delete-audio-recording="deleteAudioRecording"
        :is-recording-paused="isRecordingPaused"
        :toggle-emoji-picker="toggleEmojiPicker"
        :message="message"
        :portal-slug="connectedPortalSlug"
        :new-conversation-modal-active="newConversationModalActive"
        @select-whatsapp-template="openWhatsappTemplateModal"
        @toggle-canned-menu="toggleAceleresCannedPopover"
        @execute-copilot-action="executeCopilotAction"
        @select-content-template="openContentTemplateModal"
        @toggle-insert-article="toggleInsertArticle"
        @toggle-quoted-reply="toggleQuotedReply"
        @toggle-note-mode="onToggleNoteMode"
      />
    </Transition>

    <AceleresCannedPopover
      :show="showAceleresCannedPopover"
      :account-id="accountId"
      @close="closeAceleresCannedPopover"
      @insert="onAceleresCannedInsert"
    />

    <WhatsappTemplates
      :inbox-id="inbox.id"
      :show="showWhatsAppTemplatesModal"
      @close="hideWhatsappTemplatesModal"
      @on-send="onSendWhatsAppReply"
      @cancel="hideWhatsappTemplatesModal"
    />

    <ContentTemplates
      :inbox-id="inbox.id"
      :show="showContentTemplatesModal"
      @close="hideContentTemplatesModal"
      @on-send="onSendContentTemplateReply"
      @cancel="hideContentTemplatesModal"
    />

    <woot-confirm-modal
      ref="confirmDialog"
      :title="$t('CONVERSATION.REPLYBOX.UNDEFINED_VARIABLES.TITLE')"
      :description="undefinedVariableMessage"
    />
  </div>
</template>

<style lang="scss" scoped>
.send-button {
  @apply mb-0;
}

.reply-box {
  @apply relative mb-2 mx-2 border border-n-weak bg-n-solid-1;
  border-radius: 8px;

  &.is-private {
    @apply bg-n-solid-amber dark:border-n-amber-3/10 border-n-amber-12/5;
  }
}

.reply-box.reply-box--gallery-hidden {
  display: none !important;
}

.reply-box.reply-box--multi-line {
  height: 64px;
  min-height: 64px;
  max-height: 64px;
  overflow: hidden;
}

.send-button {
  @apply mb-0;
}

.reply-box__top {
  @apply relative px-3 pt-0.5 pb-0 flex flex-col items-stretch;
}

.emoji-dialog {
  @apply top-[unset] -bottom-10 ltr:-left-80 ltr:right-[unset] rtl:left-[unset] rtl:-right-80;

  &::before {
    filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.08));
    @apply ltr:-right-4 bottom-2 rtl:-left-4 ltr:rotate-[270deg] rtl:rotate-[90deg];
  }
}

.emoji-dialog--expanded {
  @apply left-[unset] bottom-0 absolute z-[100];

  &::before {
    transform: rotate(0deg);
    @apply ltr:left-1 rtl:right-1 -bottom-2;
  }
}

:global(.acl-composer-emoji-popper) {
  z-index: 2147483647 !important;
}

:global(.acl-composer-emoji-popper .v-popper__inner) {
  background: transparent !important;
  border: 0 !important;
  box-shadow: none !important;
}

:global(.acl-composer-emoji-popper .v-popper__arrow-container) {
  display: none !important;
}

:global(
    .acl-composer-emoji-popper .aceleres-emoji-picker-card.acl-inline-picker
  ) {
  width: min(420px, calc(100vw - 16px)) !important;
  max-width: calc(100vw - 16px) !important;
  height: min(420px, calc(100dvh - 32px)) !important;
  max-height: min(420px, calc(100dvh - 32px)) !important;
  background: #f7f8fa;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 12px;
  box-shadow:
    0 12px 28px rgba(0, 0, 0, 0.2),
    0 2px 4px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

:global(.acl-composer-emoji-popper .aceleres-emoji-picker-body) {
  width: 100%;
  height: 100%;
  overflow: hidden;
}

:global(.acl-composer-emoji-popper .emoji-dialog) {
  position: static !important;
  width: 100% !important;
  height: 100% !important;
  max-height: none !important;
  box-shadow: none !important;
  outline: none !important;
  border: 0 !important;
  border-radius: 0 !important;
}

:global(.acl-composer-emoji-popper .emoji-dialog::before) {
  display: none !important;
}

:global(.acl-composer-emoji-popper .emoji-dialog > .flex.flex-col) {
  height: 100% !important;
}

:global(.acl-composer-emoji-popper .emoji-dialog--footer) {
  background: #f7f8fa !important;
  border-bottom: 0 !important;
  padding: 10px 14px !important;
}

:global(.acl-composer-emoji-popper .emoji-dialog--footer ul) {
  gap: 0 !important;
  justify-content: space-between;
  overflow: hidden !important;
  left: 0 !important;
  padding: 2px 0 !important;
}

:global(.acl-composer-emoji-popper .emoji-dialog--footer li) {
  flex: 1 1 0;
  position: relative;
  padding: 2px !important;
}

:global(.acl-composer-emoji-popper .emoji-dialog--footer li .emoji--item) {
  width: 100% !important;
  height: 34px !important;
  color: rgba(0, 0, 0, 0.5);
  opacity: 0.65;
}

:global(.acl-composer-emoji-popper .emoji-dialog--footer li .active) {
  background: transparent !important;
  color: rgba(0, 0, 0, 0.95);
  font-weight: 700;
  opacity: 1 !important;
}

:global(.acl-composer-emoji-popper .emoji-item) {
  flex: 1 1 0%;
  height: auto !important;
  min-height: 0;
}

:global(
    .dark
      .acl-composer-emoji-popper
      .aceleres-emoji-picker-card.acl-inline-picker
  ) {
  background: #1e293b;
}
</style>
